import { Directive, ElementRef, HostListener, Injector, Input, OnInit, inject } from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  FormControlDirective,
  FormControlName,
  FormGroupDirective,
  NgControl,
  NgModel,
  Validators
} from '@angular/forms';
import { Subject, distinctUntilChanged, startWith, takeUntil, tap } from 'rxjs';

@Directive({
  selector: '[appControlValueAccessorDirective]',
  standalone: true
})
export class ControlValueAccessorDirective<T> implements ControlValueAccessor, OnInit {
  private injector: Injector = inject(Injector);
  private el: ElementRef = inject(ElementRef);

  @Input() errorText = '';
  control: FormControl | undefined;
  isRequired = false;
  isDisabled = false;
  isTouched = false;
  private _destroy$ = new Subject<void>();
  private onChange: (input: T | null) => void;
  private onTouched: () => T;

  ngOnInit(): void {
    this.setFormControl();
  }

  setFormControl() {
    const ngControl = this.injector.get(NgControl);
    if (ngControl) {
      switch (ngControl.constructor) {
        case NgModel: {
          const { control } = ngControl as NgModel;
          this.control = control;
          break;
        }
        case FormControlName:
          this.control = this.injector.get(FormGroupDirective).getControl(ngControl as FormControlName);
          break;

        default:
          this.control = (ngControl as FormControlDirective).form as FormControl;
          break;
      }
      this.isRequired = this.control?.hasValidator(Validators.required) ?? false;
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars, @typescript-eslint/no-empty-function
  writeValue(value: T): void {}

  registerOnChange(fn: (val: T | null) => T): void {
    this.control.valueChanges
      .pipe(
        takeUntil(this._destroy$),
        startWith(this.control.value),
        distinctUntilChanged(),
        tap(val => {
          val ? fn(val) : null;
        })
      )
      .subscribe();
  }

  registerOnTouched(fn: () => T): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  setNewValue(value: T) {
    this.isTouched = true;
    this.control.setValue(value);
  }

  getError() {
    const errors = this.control.errors;
    if (!this.errorText && errors)
      for (const key in errors) {
        if (Object.prototype.hasOwnProperty.call(errors, key)) {
          const element = errors[key];
          return typeof element !== 'object' && element === true ? `errors.${key}` : 'errors.required';
        }
      }
    else {
      return this.errorText;
    }
  }

  @HostListener('document:click', ['$event']) clickedOutside(event: Event) {
    if (this.el?.nativeElement !== event.target) {
      if (this.isTouched && !this.control?.touched) this.control?.markAsTouched();
    }
  }
}
